import { CommonModule } from '@angular/common';
import { Component, inject, Input, OnInit, TemplateRef } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzModalRef, NzModalService } from 'ng-zorro-antd/modal';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { ClipboardModule } from '@angular/cdk/clipboard';
import { API_KEY_PERMISSIONS, ApiKey, ApiKeyCreateInput, ApiKeySupplierMetadata, Customer } from '../../../model/models';
import { YomaDateFormatPipe } from '../../../pipes/yoma-date-format.pipe';
import { ApiKeyConfigurationComponent } from './api-key-configuration/api-key-configuration.component';
import { ApiKeysService } from './api-keys.service';
import { SupplierMetadataEditorComponent } from './supplier-metadata-editor/supplier-metadata-editor.component';

interface MetadataRow {
  key: string
  value: string
}

@Component({
  selector: 'api-keys',
  standalone: true,
  imports: [CommonModule, FormsModule, NzButtonModule, NzIconModule, NzFormModule, NzInputModule, NzSelectModule,
    NzTableModule, NzToolTipModule, ClipboardModule, YomaDateFormatPipe, SupplierMetadataEditorComponent, ApiKeyConfigurationComponent],
  templateUrl: './api-keys.component.html',
  styleUrl: './api-keys.component.scss'
})
export class ApiKeysComponent implements OnInit {
  @Input({ required: true }) customer!: Customer;

  apiKeysService = inject(ApiKeysService);
  modal = inject(NzModalService);
  notification = inject(NzNotificationService);

  permissionOptions = API_KEY_PERMISSIONS;

  apiKeys: ApiKey[] = [];
  isLoading = true;
  createKeyLoading = false;
  apiKeySecret = '';
  editingApiKey?: ApiKey;

  newApiKey: { name: string; description: string; permissions: string[] } = { name: '', description: '', permissions: ['read'] };
  metadataRows: MetadataRow[] = [];
  supplierMetadata: ApiKeySupplierMetadata[] = [];

  ngOnInit(): void {
    this.loadApiKeys();
  }

  loadApiKeys(): void {
    this.isLoading = true;
    this.apiKeysService.getApiKeys(this.customer.pk).subscribe({
      next: (res) => {
        this.apiKeys = res;
        this.isLoading = false;
      },
      error: () => {
        this.isLoading = false;
        this.notification.error('Error', 'Please, Reload the page');
      }
    });
  }

  openCreateModal(tplContent: TemplateRef<{}>, tplFooter: TemplateRef<{}>): void {
    this.modal.create({
      nzTitle: 'Generate new API key',
      nzContent: tplContent,
      nzFooter: tplFooter,
      nzMaskClosable: false,
      nzWidth: 640,
      nzOnCancel: () => this.resetCreateForm()
    });
  }

  addMetadataRow(): void {
    this.metadataRows.push({ key: '', value: '' });
  }

  removeMetadataRow(index: number): void {
    this.metadataRows.splice(index, 1);
  }

  isCreateDisabled(): boolean {
    return !this.newApiKey.name || this.newApiKey.permissions.length === 0;
  }

  create(ref: NzModalRef): void {
    this.createKeyLoading = true;
    const metadata: { [key: string]: string } = {};
    this.metadataRows.forEach(row => {
      if (row.key) {
        metadata[row.key] = row.value;
      }
    });
    const scopeSegments = this.newApiKey.permissions.map(p => p === 'read' || p === 'write' ? `${p}:vehicles` : p);
    const scope = ['openid', 'email', ...scopeSegments].join(' ');
    const payload: ApiKeyCreateInput = {
      name: this.newApiKey.name,
      description: this.newApiKey.description,
      permissions: this.newApiKey.permissions,
      scope,
      filter: [],
      metadata,
      supplier_metadata: this.supplierMetadata.filter(e => e.supplier_system_partner_id && e.supplier_partner_id)
    };
    this.apiKeysService.createApiKey(this.customer.pk, payload).subscribe({
      next: (res) => {
        this.apiKeySecret = res.raw_key;
        this.createKeyLoading = false;
        ref.close();
        this.resetCreateForm();
        this.loadApiKeys();
      },
      error: () => {
        this.createKeyLoading = false;
        this.notification.error('Error', 'Failed to create API key');
      }
    });
  }

  resetCreateForm(): void {
    this.newApiKey = { name: '', description: '', permissions: ['read'] };
    this.metadataRows = [];
    this.supplierMetadata = [];
  }

  deleteApiKey(apiKey: ApiKey): void {
    const notification = this.notification;
    this.modal.warning({
      nzTitle: `Delete API key "${apiKey.name}"?`,
      nzContent: 'This action cannot be undone.',
      nzOkText: 'Yes',
      nzOkDanger: true,
      nzCancelText: 'No',
      nzOnOk: () => new Promise<void>(resolve => {
        this.apiKeysService.deleteApiKey(this.customer.pk, apiKey.api_key_id).subscribe({
          next: () => {
            this.loadApiKeys();
            notification.success('Success', `API key "${apiKey.name}" deleted`);
          },
          error: (err: Error) => {
            notification.error('Error', `There was an error ${err.message}`);
          },
          complete: () => resolve()
        });
      })
    });
  }

  editApiKey(apiKey: ApiKey): void {
    this.editingApiKey = structuredClone(apiKey);
  }

  back(): void {
    this.editingApiKey = undefined;
    this.loadApiKeys();
  }
}
